// useToolTipSequence.ts
import { useState, useMemo, useCallback, useEffect } from 'react';
import { TOOLTIP_STEPS } from './toolTipSteps';

/**
 * Shared global state for tooltip sequence.
 * Ensures all TooltipSequence instances use the same progress.
 */
let globalStepIndex = 0;
let subscribers: React.Dispatch<React.SetStateAction<number>>[] = [];

function notifySubscribers() {
  subscribers.forEach(setState => setState(globalStepIndex));
}

export function useTooltipSequence() {
  const [stepIndex, setStepIndex] = useState(globalStepIndex);

  // 🔄 Keep hook state synced with global state
  useEffect(() => {
    subscribers.push(setStepIndex);
    return () => {
      subscribers = subscribers.filter(s => s !== setStepIndex);
    };
  }, []);

  const currentStep = useMemo(() => TOOLTIP_STEPS[stepIndex] ?? null, [stepIndex]);
  const isFinished = useMemo(() => stepIndex >= TOOLTIP_STEPS.length, [stepIndex]);

  const next = useCallback(() => {
    globalStepIndex = Math.min(globalStepIndex + 1, TOOLTIP_STEPS.length);
    notifySubscribers();
  }, []);

  const close = useCallback(() => {
    globalStepIndex = TOOLTIP_STEPS.length; // mark as finished
    notifySubscribers();
  }, []);

  const setStage = useCallback((stage: number) => {
    if (Number.isFinite(stage) && stage >= 0 && stage < TOOLTIP_STEPS.length) {
      globalStepIndex = stage;
      notifySubscribers();
    }
  }, []);

  const reset = useCallback(() => {
    globalStepIndex = 0;
    notifySubscribers();
  }, []);

  return {
    stepIndex,
    currentStep,
    isFinished,
    next,
    close,
    setStage,
    reset,
  };
}
